/**
 * Browser -> R2 multipart upload against `POST /v1/uploads` (+ `/parts`, `/complete`, `/cancel`).
 * Parts go straight to the presigned URLs with XHR (for progress); 3 in flight; each part retried.
 * Resume: the session (upload id, part size, etags so far) is kept in localStorage so a reload
 * can continue once the user picks the SAME file again (a browser cannot keep a File across reloads).
 */
import { ApiError, post } from "../api/client";
import { noteError } from "./errlog";
import type { BetaRequest, UploadComplete, UploadCreate, YoutubeRequest } from "../api/types";

export interface UploadSession {
  upload_id: string;
  video_id: string;
  filename: string;
  bytes: number;
  last_modified: number;
  part_size: number;
  parts_expected: number;
  etags: Record<number, string>;
  title: string;
  team_id?: string | null;
}

const KEY = "dt.upload.session";
export const loadSession = (key = KEY): UploadSession | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as UploadSession) : null;
  } catch {
    return null;
  }
};
const saveSession = (s: UploadSession | null, key = KEY) => (s ? localStorage.setItem(key, JSON.stringify(s)) : localStorage.removeItem(key));

/**
 * Where an upload is created and where its resume record is kept. A live session's segment
 * (2026-10-18) is created by `POST /ops/live/sessions/{id}/segments`; its parts and completion
 * then go through `/v1/uploads` like any upload.
 */
export interface UploadTarget { createPath: string; storeKey: string }
const DEFAULT_TARGET: UploadTarget = { createPath: "/v1/uploads", storeKey: KEY };

export interface Progress { sent: number; total: number; parts_done: number; parts_expected: number }

export function putPart(url: string, blob: Blob, onProgress: (loaded: number) => void, signal: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader("ETag");
        if (!etag) reject(new Error("part upload returned no ETag (R2 CORS must expose ETag)"));
        else resolve(etag);
      } else reject(new Error(`part upload failed: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("part upload failed: network"));
    xhr.onabort = () => reject(new DOMException("aborted", "AbortError"));
    signal.addEventListener("abort", () => xhr.abort());
    xhr.send(blob);
  });
}

export async function readDurationSeconds(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    const url = URL.createObjectURL(file);
    const done = (d: number | null) => {
      URL.revokeObjectURL(url);
      resolve(d);
    };
    v.onloadedmetadata = () => done(Number.isFinite(v.duration) && v.duration > 0 ? v.duration : null);
    v.onerror = () => done(null);
    setTimeout(() => done(null), 8000);
    v.src = url;
  });
}

export function sessionMatches(s: UploadSession | null, file: File, teamId?: string | null): s is UploadSession {
  return !!s && s.filename === file.name && s.bytes === file.size && s.last_modified === file.lastModified && (s.team_id ?? null) === (teamId ?? null);
}

let running = 0;

/** Keep build refreshes from interrupting a direct multipart media upload. */
export function beginUploadActivity(): () => void {
  running++;
  let active = true;
  return () => { if (active) { active = false; running--; } };
}

/** A short FNV-1a digest of the choices sent with an upload, for its Idempotency-Key. */
function digest(v: unknown): string {
  let h = 0x811c9dc5;
  for (const ch of JSON.stringify(v)) {
    h ^= ch.codePointAt(0)!;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

export interface UploadOptions { pitch_length_choice: "full" | "junior" | "unknown"; duration_hint_s: number; title?: string; youtube?: YoutubeRequest; team_id?: string | null; beta?: BetaRequest }

/** Whether an upload runs in this tab: a full page load would stop it (lib/freshness.ts waits). */
export function uploadInProgress(): boolean {
  return running > 0;
}

export class Uploader {
  private ctl = new AbortController();
  constructor(private readonly file: File, private readonly onProgress: (p: Progress) => void, private readonly target: UploadTarget = DEFAULT_TARGET) {}

  cancelLocal(): void {
    this.ctl.abort();
  }

  /** Create (or resume) the session, upload every missing part, complete. */
  async run(opts: UploadOptions): Promise<{ session: UploadSession; result: UploadComplete }> {
    running++;
    try {
      return await this.runOnce(opts);
    } finally {
      running--;
    }
  }

  private async runOnce(opts: UploadOptions): Promise<{ session: UploadSession; result: UploadComplete }> {
    const store = this.target.storeKey;
    const teamId = opts.team_id ?? null;
    let s = loadSession(store);
    if (sessionMatches(s, this.file, teamId)) {
      try {
        await this.uploadParts(s, new Map());
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status === 409 || e.status === 410)) {
          saveSession(null, store);
          s = null;
        } else {
          throw e;
        }
      }
    }
    if (!sessionMatches(s, this.file, teamId)) {
      const created = await post<UploadCreate>(this.target.createPath, {
        filename: this.file.name,
        bytes: this.file.size,
        duration_hint_s: opts.duration_hint_s,
        pitch_length_choice: opts.pitch_length_choice,
        ...(opts.title ? { title: opts.title } : {}),
        ...(opts.youtube ? { youtube: opts.youtube } : {}),
        ...(opts.beta ? { beta: opts.beta } : {}),
        ...(teamId ? { team_id: teamId } : {}),
        // The key is the file's, on purpose: Upload stays clickable while the duration is read, and a
        // second click must get the same upload. The name is percent-encoded because fetch() refuses a
        // header with a character above U+00FF, e.g. the "⧸" yt-dlp writes for "/" in a title. The
        // pitch and YouTube choices are in it too: the API refuses a key used again with other choices
        // (409), which an upload cancelled and started again with the switch on would be.
      }, { "Idempotency-Key": `upl-${encodeURIComponent(this.file.name)}-${this.file.size}-${this.file.lastModified}-${digest([opts.pitch_length_choice, opts.youtube ?? null, teamId, opts.beta ?? null])}` });
      s = { upload_id: created.upload_id, video_id: created.video_id, filename: this.file.name, bytes: this.file.size, last_modified: this.file.lastModified, part_size: created.part_size, parts_expected: created.parts_expected, etags: {}, title: opts.title ?? this.file.name, team_id: teamId };
      saveSession(s, store);
      await this.uploadParts(s, new Map(created.part_urls.map((p) => [p.n, p.url])));
    }
    const parts = Object.entries(s.etags).map(([n, etag]) => ({ n: Number(n), etag })).sort((a, b) => a.n - b.n);
    const result = await post<UploadComplete>(`/v1/uploads/${s.upload_id}/complete`, { parts }, { "Idempotency-Key": `cmp-${s.upload_id}` });
    saveSession(null, store);
    return { session: s, result };
  }

  private async uploadParts(s: UploadSession, urls: Map<number, string>): Promise<void> {
    const signal = this.ctl.signal;
    const loaded = new Map<number, number>();
    const doneBytes = () => Object.keys(s.etags).reduce((acc, n) => acc + this.partBytes(s, Number(n)), 0);
    const report = () => this.onProgress({ sent: Math.min(s.bytes, doneBytes() + [...loaded.values()].reduce((a, b) => a + b, 0)), total: s.bytes, parts_done: Object.keys(s.etags).length, parts_expected: s.parts_expected });
    report();
    const missing: number[] = [];
    for (let n = 1; n <= s.parts_expected; n++) if (!s.etags[n]) missing.push(n);
    const ensureUrl = async (n: number): Promise<string> => {
      const have = urls.get(n);
      if (have) return have;
      const from = n;
      const to = Math.min(s.parts_expected, n + 99);
      const res = await post<{ part_urls: { n: number; url: string }[] }>(`/v1/uploads/${s.upload_id}/parts`, { from, to });
      for (const p of res.part_urls) urls.set(p.n, p.url);
      return urls.get(n)!;
    };
    let cursor = 0;
    const worker = async () => {
      while (cursor < missing.length) {
        if (signal.aborted) throw new DOMException("aborted", "AbortError");
        const n = missing[cursor++]!;
        const start = (n - 1) * s.part_size;
        const blob = this.file.slice(start, Math.min(s.bytes, start + s.part_size));
        let attempt = 0;
        for (;;) {
          try {
            const url = await ensureUrl(n);
            const etag = await putPart(url, blob, (l) => {
              loaded.set(n, l);
              report();
            }, signal);
            s.etags[n] = etag;
            loaded.delete(n);
            saveSession(s, this.target.storeKey);
            report();
            break;
          } catch (e) {
            if ((e as Error).name === "AbortError") throw e;
            if (e instanceof ApiError && (e.status === 404 || e.status === 409 || e.status === 410)) throw e;
            noteError(`upload part ${n}`, (e as Error).message);
            if (++attempt >= 4) throw e;
            urls.delete(n);
            await new Promise((r) => setTimeout(r, 500 * attempt));
          }
        }
      }
    };
    await Promise.all([worker(), worker(), worker()]);
  }

  private partBytes(s: UploadSession, n: number): number {
    return n === s.parts_expected ? s.bytes - (n - 1) * s.part_size : s.part_size;
  }
}

export async function cancelUpload(s: UploadSession): Promise<void> {
  try {
    await post(`/v1/uploads/${s.upload_id}/cancel`);
  } finally {
    saveSession(null);
  }
}

export const clearSession = () => saveSession(null);
